import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.attributes.*

internal val TOUR_GROUPS = listOf("" to "Čeká na přiřazení") + "ABCDEF".map { it.toString() to it.toString() }
internal fun tourAverage(value: Double?) = value?.let(::formatAvg) ?: "—"
internal fun handicapSetLabel(set: TourHandicapSetDto) = "${formatDate(set.effectiveFrom)} · ${set.sourceSeasonName} · " +
    when (set.status) { "draft" -> "náhled"; "confirmed" -> "potvrzená"; else -> "nahrazená" }

@Composable
internal fun TourSeasonScope(token: String?, showSeason: Boolean = true, includeFinished: Boolean = false, content: @Composable (TourSeasonDto, List<TourTournamentDto>) -> Unit) {
    var seasons by remember { mutableStateOf<List<TourSeasonDto>>(emptyList()) }
    var selected by remember { mutableStateOf("") }
    var tournaments by remember { mutableStateOf<List<TourTournamentDto>>(emptyList()) }
    var loadedSeason by remember { mutableStateOf("") }
    var error by remember { mutableStateOf<String?>(null) }
    var loading by remember { mutableStateOf(true) }
    var retry by remember { mutableStateOf(0) }
    LaunchedEffect(retry) {
        loading = true; error = null
        try {
            seasons = TourApi.seasons(token)
            selected = (currentTourSeason(seasons, admin = token != null)
                ?: if (includeFinished) seasons.firstOrNull { it.status == "finished" } else null)?.id ?: ""
        } catch (e: Throwable) { if (e is CancellationException) throw e; error = e.apiMessage() }
        finally { loading = false }
    }
    LaunchedEffect(selected, retry) {
        loadedSeason = ""; tournaments = emptyList()
        if (selected.isNotEmpty()) try {
            tournaments = TourApi.tournaments(token, selected); loadedSeason = selected
        } catch (e: Throwable) { if (e is CancellationException) throw e; error = e.apiMessage() }
    }
    val loadingContent = loading || (selected.isNotEmpty() && loadedSeason != selected && error == null)
    if (showSeason || loadingContent || error != null || selected.isEmpty()) Div(attrs = { classes("card") }) {
        if (loadingContent) P { Text("Načítám…") }
        TourError(error)
        if (error != null) Button(attrs = { onClick { retry++ } }) { Text("Načíst znovu") }
        if (!loading && error == null && selected.isEmpty()) P { Text(if (token == null) "Ročník Tour zatím nebyl zveřejněn." else "V záložce Ročníky založte nebo aktivujte aktuální ročník Tour.") }
        if (showSeason) seasons.firstOrNull { it.id == selected }?.let { season ->
            P { Text(season.name) }
            if (season.status == "draft") P { Text("Pracujete v konceptu. Pro zveřejnění nastavte ročník jako probíhající.") }
        }
    }
    seasons.firstOrNull { it.id == selected }?.takeIf { loadedSeason == it.id }?.let { season ->
        key(season.id) { content(season, tournaments) }
    }
}

@Composable
fun TourHandicapsAdmin(token: String) {
    TourSeasonScope(token, showSeason = false) { season, tournaments -> TourHandicapEditor(token, season, tournaments) }
}

@Composable
private fun TourHandicapEditor(token: String, season: TourSeasonDto, tournaments: List<TourTournamentDto>) {
    val halfStarts = tournaments.filter { it.sequenceNo == 1 || it.sequenceNo == 5 }
    var sources by remember { mutableStateOf<List<SeasonDto>>(emptyList()) }
    var players by remember { mutableStateOf<List<TourPlayerDto>>(emptyList()) }
    var sets by remember { mutableStateOf<List<TourHandicapSetDto>>(emptyList()) }
    var values by remember { mutableStateOf<List<TourHandicapDto>>(emptyList()) }
    var rosterIds by remember { mutableStateOf<Set<String>>(emptySet()) }
    var selected by remember { mutableStateOf("") }
    var sourceId by remember { mutableStateOf("") }
    var firstId by remember { mutableStateOf(halfStarts.firstOrNull { it.startsOn != null }?.id ?: "") }
    var requestId by remember { mutableStateOf(window.asDynamic().crypto.randomUUID() as String) }
    var search by remember { mutableStateOf("") }
    var editing by remember { mutableStateOf<TourHandicapDto?>(null) }
    var addPlayer by remember { mutableStateOf(false) }
    var manualEditing by remember { mutableStateOf(false) }
    var refresh by remember { mutableStateOf(0) }
    var loading by remember { mutableStateOf(true) }
    var busy by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var message by remember { mutableStateOf<String?>(null) }
    var importOpen by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    LaunchedEffect(refresh) {
        loading = true; error = null
        try {
            coroutineScope {
                val allSources = async { Api.fetchSeasons(null) }
                val allPlayers = async { TourApi.players(token) }
                val allSets = async { TourOperationsApi.sets(token, season.id) }
            sources = allSources.await(); players = allPlayers.await(); sets = allSets.await().filter { it.status != "superseded" }
            }
            if (sets.none { it.id == selected }) selected = (sets.firstOrNull { it.status == "confirmed" } ?: sets.firstOrNull())?.id ?: ""
        } catch (e: Throwable) { if (e is CancellationException) throw e; error = e.apiMessage() }
        finally { loading = false }
    }
    val currentSet = sets.firstOrNull { it.id == selected }
    LaunchedEffect(selected, currentSet?.revision) {
        values = emptyList(); rosterIds = emptySet(); editing = null; addPlayer = false
        if (selected.isNotEmpty()) {
            loading = true
            try { coroutineScope {
                val handicaps = async { TourOperationsApi.handicaps(token, selected) }
                val roster = async { TourOperationsApi.handicapRoster(token, selected) }
                values = handicaps.await(); rosterIds = roster.await()
            } }
            catch (e: Throwable) { if (e is CancellationException) throw e; error = e.apiMessage() }
            finally { loading = false }
        }
    }
    fun operate(action: suspend () -> Unit) {
        if (busy) return
        scope.launch {
            busy = true; error = null; message = null
            try { action(); refresh++ }
            catch (e: Throwable) { if (e is CancellationException) throw e; error = e.apiMessage() }
            finally { busy = false }
        }
    }
    Div(attrs = { classes("card", "tour-admin-heading") }) {
        H2 { Text("Handicapy · ${season.name}") }
        Div(attrs = { classes("tour-actions") }) {
            Button(attrs = { attr("aria-expanded", importOpen.toString()); onClick { importOpen = !importOpen } }) { Text(if (importOpen) "Zavřít převzetí průměrů" else "Převzít průměry z ligy") }
            Button(attrs = { if (busy || loading) disabled(); onClick { refresh++ } }) { Text("Načíst aktuální hodnoty") }
        }
        if (loading && currentSet == null) P { Text("Načítám handicapy…") }
        TourError(if (editing == null && !addPlayer) error else null)
        message?.let { P(attrs = { attr("role", "status") }) { Text(it) } }
    }
    if (importOpen || (!loading && sets.isEmpty())) Div(attrs = { classes("card") }) {
        H2 { Text("Převzetí průměrů z ligy") }
        P { Text("Vygenerují se pouze hráči na soupiskách aktuální ligové sezóny. Průměry se převezmou ze zvolené zdrojové sezóny; ženám se odečte 8 bodů. Bez minima her. Hosty mimo soupisky lze přidat ručně.") }
        Div(attrs = { classes("tour-form") }) {
            TourChoice("Zdrojová ligová sezóna", sourceId, listOf("" to "Vyberte sezónu") + sources.map { it.id to it.name }, busy) { sourceId = it; requestId = window.asDynamic().crypto.randomUUID() as String }
            TourChoice("Polovina sezóny", firstId, listOf("" to "Vyberte polovinu sezóny") + halfStarts.filter { it.startsOn != null }.map {
                it.id to "${if (it.sequenceNo == 1) "1. polovina · turnaje 1–4" else "2. polovina · turnaje 5–8"} · od ${it.startsOn?.let(::formatDate)}"
            }, busy) { firstId = it; requestId = window.asDynamic().crypto.randomUUID() as String }
            Button(attrs = { classes("primary");
                if (busy || loading || sourceId.isEmpty() || firstId.isEmpty() || season.status == "finished") disabled()
                onClick { val id = requestId; operate {
                    TourOperationsApi.prepare(token, id, season.id, sourceId, firstId)
                    selected = id; requestId = window.asDynamic().crypto.randomUUID() as String
                    message = "Náhled byl uložen. Zkontrolujte skupiny a potvrďte platnost sady."; importOpen = false
                } }
            }) { Text("Připravit náhled handicapů") }
        }
    }
    if (currentSet != null) {
        val missing = players.filter { p -> p.id in rosterIds && values.none { it.playerId == p.id } }.map { p ->
            TourHandicapDto(p.id, currentSet.id, p.id, p.name, gender = p.gender)
        }
        val all = (values.filter { it.playerId in rosterIds || it.isManual } + missing).sortedWith(compareBy(tourNameComparator) { it.playerName })
        Div(attrs = { classes("card") }) {
            H2 { Text("Zařazení hráčů") }
            TourChoice("Sada handicapů", selected, sets.map { it.id to handicapSetLabel(it) }, busy) { selected = it }
            P(attrs = { classes("muted") }) { Text("Rozhodné období: ${currentSet.sourceSeasonName} · platnost od ${formatDate(currentSet.effectiveFrom)}.") }
            if (loading) P { Text("Načítám hodnoty…") }
            P { Text("Hráčů: ${all.size} · čeká na přiřazení: ${all.count { it.groupCode == null }} · ruční výjimky: ${all.count { it.isManual }}") }
            if (currentSet.status == "draft") Button(attrs = { classes("primary");
                if (busy || loading || season.status == "finished") disabled()
                onClick { operate { TourOperationsApi.confirm(token, currentSet); message = "Sada potvrzena. Odehrané ani zahájené rundy se nepřepočítávají." } }
            }) { Text("Potvrdit sadu od ${formatDate(currentSet.effectiveFrom)}") }
            TourField("Hledat v handicapech", search, required = false) { search = it }
            Div(attrs = { classes("tour-admin-toolbar", "tour-handicap-toolbar") }) {
                Label {
                    Input(type = InputType.Checkbox) { checked(manualEditing); onChange { manualEditing = it.value } }
                    Text("Ruční editace")
                }
                if (season.status != "finished") Button(attrs = {
                    if (busy || loading) disabled(); onClick { error = null; addPlayer = true }
                }) { Text("Ruční přiřazení hráče") }
            }
            if (editing != null || addPlayer) key(editing?.playerId) {
                TourHandicapDialog(editing, players.filter { p -> all.none { it.playerId == p.id } }, values, busy || loading, error,
                    onClose = { editing = null; addPlayer = false }, onSave = { playerId, group, reason -> operate {
                        TourOperationsApi.assign(token, currentSet, playerId, group.ifBlank { null }, reason)
                        editing = null; addPlayer = false; message = "Ruční zařazení uloženo."
                    } })
            }
            Table(attrs = { classes("tour-admin-table") }) {
                Tr { listOf("Hráč", "Hry", "Průměr", "Skupina", "").forEach { Th { Text(it) } } }
                all.filter { it.playerName.contains(search, ignoreCase = true) }.forEach { row -> Tr {
                    Td(attrs = { classes("tour-player-name") }) { Text(row.playerName) }
                    Td { Text(row.games.toString()) }
                    Td { Text(tourAverage(row.adjustedAverage)) }
                    Td {
                        Text(row.groupCode ?: "Čeká na přiřazení")
                        if (row.isManual) Span(attrs = { classes("tour-result-flag"); title(row.reason) }) { Text("Ruční zařazení") }
                        if (row.gender == null) Span(attrs = { classes("tour-result-flag") }) { Text("Chybí pohlaví") }
                    }
                    Td { if (currentSet.status != "superseded" && season.status != "finished" && (row.groupCode == null || manualEditing)) Button(attrs = {
                        if (busy || loading) disabled()
                        attr("aria-label", "Zařadit ${row.playerName}"); onClick { error = null; editing = row }
                    }) { Text("Zařadit") } }
                } }
            }
        }

    }
}

@Composable
fun TourPublicHandicaps() {
    TourSeasonScope(null, showSeason = false, includeFinished = true) { season, tournaments -> key(season.id) {
        var periods by remember { mutableStateOf<List<Pair<Int, TourHandicapSetDto>>>(emptyList()) }
        var values by remember { mutableStateOf<Map<String, List<TourHandicapDto>>>(emptyMap()) }
        var search by remember { mutableStateOf("") }
        var error by remember { mutableStateOf<String?>(null) }
        var loading by remember { mutableStateOf(true) }
        var retry by remember { mutableStateOf(0) }
        LaunchedEffect(retry) {
            loading = true; error = null
            try {
                val sets = TourOperationsApi.sets(null, season.id).filter { it.status == "confirmed" }
                val secondStart = tournaments.firstOrNull { it.sequenceNo == 5 }
                // Internal revisions remain available to administration; the public sees two fixed periods.
                periods = listOf(1, 2).mapNotNull { half ->
                    sets.filter { set ->
                        val first = tournaments.firstOrNull { it.id == set.firstTournamentId }
                        val setHalf = when (first?.sequenceNo) {
                            1 -> 1
                            5 -> 2
                            else -> if (secondStart?.startsOn?.let { set.effectiveFrom >= it } == true) 2 else 1
                        }
                        setHalf == half
                    }.maxByOrNull { it.createdAt }?.let { half to it }
                }
                values = coroutineScope {
                    periods.map { (_, set) -> async { set.id to TourOperationsApi.publicHandicaps(set.id) } }.map { it.await() }.toMap()
                }
            } catch (e: Throwable) { if (e is CancellationException) throw e; error = e.apiMessage() }
            finally { loading = false }
        }
        Div(attrs = { classes("card") }) {
            H2 { Text("Handicapy · ${season.name}") }
            P { Text("Handicap na jednu hru podle skupiny a disciplíny.") }
            Div(attrs = { classes("table-scroll") }) {
                Table(attrs = { classes("public-table", "tour-score-table"); attr("aria-label", "Handicapy podle skupin") }) {
                    Thead { Tr { listOf("Skupina", "Průměr pro zařazení", "Klasika", "Devítky", "Osmičky", "Narubák").forEach { Th { Text(it) } } } }
                    Tbody {
                        val conditions = listOf("Do 130 včetně", "Nad 130 až 140 včetně", "Nad 140 až 150 včetně", "Nad 150 až 160 včetně", "Nad 160 až 170 včetně", "Nad 170")
                        "ABCDEF".forEachIndexed { index, group -> Tr {
                            Td { Span(attrs = { classes("pill") }) { Text(group.toString()) } }
                            Td(attrs = { classes("tour-player-name") }) { Text(conditions[index]) }
                            listOf("classic", "nines", "eights", "narubak").forEach { discipline -> Td { Text(tourHandicapValue(group.toString(), discipline).toString()) } }
                        } }
                    }
                }
            }
            P(attrs = { classes("muted") }) { Text("Pro zařazení se používá ligový průměr za rozhodné období; ženám se odečítá 8 bodů. Bez minima odehraných her. Ruční zařazení může upravit správce.") }
        }
        Div(attrs = { classes("card") }) {
            H2 { Text("Zařazení hráčů · ${season.name}") }
            TourError(error)
            if (error != null) Button(attrs = { onClick { retry++ } }) { Text("Načíst znovu") }
            if (loading) P { Text("Načítám…") }
            else if (error == null && periods.isEmpty()) P { Text("Handicapy zatím nebyly nastaveny.") }
            else if (error == null) {
                periods.forEach { (half, set) ->
                    P(attrs = { classes("muted") }) {
                        Text("$half. polovina · turnaje ${if (half == 1) "1–4" else "5–8"} · rozhodné období: ${set.sourceSeasonName} · od ${formatDate(set.effectiveFrom)}")
                    }
                }
                TourField("Hledat hráče", search, required = false) { search = it }
                val players = values.values.flatten().associate { it.playerId to it.playerName }.toList().sortedWith(compareBy(tourNameComparator) { it.second })
                val bySet = values.mapValues { (_, rows) -> rows.associateBy { it.playerId } }
                Div(attrs = { classes("table-scroll") }) {
                    Table(attrs = { classes("public-table", "tour-score-table"); attr("aria-label", "Zařazení hráčů do handicapových skupin") }) {
                        Thead {
                            Tr {
                                Th(attrs = { rowspan(2); attr("scope", "col") }) { Text("Hráč") }
                                periods.forEach { (half, _) -> Th(attrs = { colspan(2); attr("scope", "colgroup"); classes("tour-group-start") }) { Text("$half. polovina sezóny") } }
                            }
                            Tr { periods.forEach { _ ->
                                Th(attrs = { classes("tour-group-start"); attr("scope", "col") }) { Text("Skupina") }
                                Th(attrs = { attr("scope", "col") }) { Text("Průměr") }
                            } }
                        }
                        Tbody { players.filter { it.second.contains(search, ignoreCase = true) }.forEach { (id, name) -> Tr {
                            Td(attrs = { classes("tour-player-name") }) { Text(name) }
                            periods.forEach { (_, set) ->
                                val row = bySet[set.id]?.get(id)
                                Td(attrs = { classes("tour-group-start") }) { Text(row?.groupCode ?: "Čeká na přiřazení") }
                                Td { Text(tourAverage(row?.adjustedAverage)) }
                            }
                        } } }
                    }
                }
                if (players.none { it.second.contains(search, ignoreCase = true) }) P { Text("Nenalezen žádný hráč.") }
                P(attrs = { classes("muted") }) { Text("Průměr je uveden po odpočtu 8 bodů ženám. Pomlčka znamená chybějící průměr; skupinu může správce přiřadit ručně.") }
            }
        }
    } }
}
